import {
  computed,
  DestroyRef,
  inject,
  Injectable,
  linkedSignal,
  ResourceLoaderParams,
  ResourceRef,
  Signal,
  signal,
  untracked,
  WritableSignal,
} from '@angular/core';
import { rxResource, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { B2bOrganizationsApiService } from '@lentau/profile/organizations/api/b2b-organizations-api.service';
import { ToastService } from '@lentau/ui-kit/molecules/toast/service/toast.service';
import { Subject, catchError, EMPTY, map, Observable, of, tap } from 'rxjs';
import { SelectProfileRequestParams } from '@lentau/profile/shared/interfaces/profile-common-info';
import { ResponseErrorListItemProps } from '@lentau/api/shared/monolith/types';
import { getParams } from '../shared/helper';
import { B2BService } from 'angular-api-gateway/v1/api/b2B.service';
import { defaultApiGatewayParams } from '@lentau/core/api/gateway';
import { GetLegalEntityListResponse } from 'angular-api-gateway/v1/model/getLegalEntityListResponse';
import { LegalEntityListItem } from 'angular-api-gateway/v1/model/legalEntityListItem';
import { ToasterErrorMessage } from '@lentau/ui-kit/molecules/toast/shared/toast.types';

type OrganizationsLoadMode = 'initial' | 'refresh';

type OrganizationsLoadRequest = {
  token: number;
  mode: OrganizationsLoadMode;
};

type OrganizationsLoadResult = {
  list: LegalEntityListItem[];
  errorList: ResponseErrorListItemProps[];
  failed: boolean;
};

type OrganizationsListSource = {
  result: OrganizationsLoadResult | undefined;
  hasRequest: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class B2BOrganizationsStore {
  private readonly b2bOrganizationsApiService: B2bOrganizationsApiService = inject(B2bOrganizationsApiService);
  private readonly api: B2BService = inject(B2BService);
  private readonly toastService: ToastService = inject(ToastService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  private readonly loadRequest: WritableSignal<OrganizationsLoadRequest | undefined> = signal(undefined);
  private readonly organizationsInit: WritableSignal<boolean> = signal(false);
  private readonly hasErrorSignal: WritableSignal<boolean> = signal(false);

  private readonly organizationsResource: ResourceRef<OrganizationsLoadResult | undefined> = rxResource<
    OrganizationsLoadResult | undefined,
    OrganizationsLoadRequest | undefined
  >({
    params: (): OrganizationsLoadRequest | undefined => this.loadRequest(),
    stream: ({
      params,
    }: ResourceLoaderParams<OrganizationsLoadRequest | undefined>): Observable<OrganizationsLoadResult> => {
      if (!params) {
        return EMPTY;
      }

      return this.api.getB2bLegalEntityList({ ...defaultApiGatewayParams }).pipe(
        map(
          (organizationsList: GetLegalEntityListResponse): OrganizationsLoadResult => ({
            list: organizationsList.list ?? [],
            errorList: [],
            failed: false,
          })
        ),
        catchError((errorList: ResponseErrorListItemProps[]): Observable<OrganizationsLoadResult> => {
          return of({
            list: this.organizations() ?? [],
            errorList: Array.isArray(errorList) ? errorList : [],
            failed: true,
          });
        }),
        tap((result: OrganizationsLoadResult): void => {
          if (!result.failed && !this.organizationsInit()) {
            this.organizationsInit.set(true);
          }

          if (params.mode === 'initial') {
            this.hasErrorSignal.set(result.failed);
          }
        })
      );
    },
  });

  public readonly organizations: Signal<LegalEntityListItem[] | null> = linkedSignal<
    OrganizationsListSource,
    LegalEntityListItem[] | null
  >({
    source: (): OrganizationsListSource => ({
      result: this.organizationsResource.value(),
      hasRequest: this.loadRequest() != null,
    }),
    computation: (
      source: OrganizationsListSource,
      previous?: { source: OrganizationsListSource; value: LegalEntityListItem[] | null }
    ): LegalEntityListItem[] | null => {
      if (source.result !== undefined) {
        return source.result.list;
      }

      if (!source.hasRequest) {
        return null;
      }

      return previous?.value ?? null;
    },
  });

  public readonly errorList: Signal<ResponseErrorListItemProps[]> = computed((): ResponseErrorListItemProps[] => {
    return this.organizationsResource.value()?.errorList ?? [];
  });

  public readonly isOrganizationsInit: Signal<boolean> = this.organizationsInit.asReadonly();
  public readonly isLoading: Signal<boolean> = computed((): boolean => {
    const request: OrganizationsLoadRequest | undefined = this.loadRequest();

    if (request?.mode === 'refresh') {
      return false;
    }

    return this.organizationsResource.isLoading();
  });

  public readonly hasError: Signal<boolean> = this.hasErrorSignal.asReadonly();

  public selectProfileForAuthorizedSuccess$: Subject<SelectProfileRequestParams> =
    new Subject<SelectProfileRequestParams>();
  public selectProfileForAuthorizedFail$: Subject<void> = new Subject<void>();

  public getOrganizationsOnAccount(): void {
    this.hasErrorSignal.set(false);
    this.triggerLoad('initial');
  }

  public updateOrganizationsOnAccount(): void {
    this.triggerLoad('refresh');
  }

  public selectProfileForAuthorized(params: SelectProfileRequestParams): void {
    this.hasErrorSignal.set(false);
    this.b2bOrganizationsApiService
      .selectProfile(getParams(params))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (): void => {
          this.hasErrorSignal.set(false);
          this.selectProfileForAuthorizedSuccess$.next(params);
        },
        error: (): void => {
          this.hasErrorSignal.set(true);
          this.toastService.show([new ToasterErrorMessage('Что-то пошло не так попробуйте позднее')]);
          this.selectProfileForAuthorizedFail$.next();
        },
      });
  }

  public setOrganizationsInit(): void {
    this.organizationsInit.set(true);
  }

  public resetAll(): void {
    this.loadRequest.set(undefined);
    this.organizationsInit.set(false);
    this.hasErrorSignal.set(false);
    this.organizationsResource.set(undefined);
  }

  private triggerLoad(mode: OrganizationsLoadMode): void {
    const nextToken: number = untracked((): number => (this.loadRequest()?.token ?? 0) + 1);
    this.loadRequest.set({ token: nextToken, mode });
  }
}
